/*
Copyright (C) 2022  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import React from "react";
import styled from "styled-components";
import { observer } from "mobx-react";
import { onSwiftRefresh } from "@src/components/modules/TemplateModule/TopBar/refresh";
import { useNavigate } from "react-router";

import MainTemplate from "@src/components/modules/TemplateModule/MainTemplate";
import Navigation from "@src/components/modules/NavigationModule/Navigation";
import FilterList from "@src/components/ui/Lists/FilterList";
import PageHeader from "@src/components/smart/PageHeader";
import configLoader from "@src/utils/Config";
import metalHubStore from "@src/stores/MetalHubStore";
import { MetalHubServer } from "@src/@types/MetalHub";
import MetalHubServerListItem from "@src/components/modules/MetalHubModule/MetalHubListItem";

import EmptyState from "@src/components/ui/Lists/EmptyState";
import {
  METAL_HUB_COLUMNS,
  serverAddress,
} from "@src/components/modules/MetalHubModule/MetalHubListItem/MetalHubListItem";
import {
  ClearFiltersButton,
  Segmented,
  ToolbarSearch,
  matchesSearch,
  useListQuery,
} from "@src/components/ui/Lists/ListToolbar";
import type { ListQuery } from "@src/components/ui/Lists/ListToolbar";
import { Server, ServerCrash } from "lucide-react";
import MetalHubListHeader from "@src/components/modules/MetalHubModule/MetalHubListHeader";
import projectStore from "@src/stores/ProjectStore";
import MetalHubModal from "@src/components/modules/MetalHubModule/MetalHubModal";
import type { DropdownAction } from "@src/components/ui/Dropdowns/ActionDropdown";
import AlertModal from "@src/components/ui/AlertModal";
import { ThemePalette } from "@src/components/Theme";
import instanceSource from "@src/sources/InstanceSource";
import instanceStore from "@src/stores/InstanceStore";
import notificationStore from "@src/stores/NotificationStore";
import { WizardData } from "@src/@types/WizardData";
import { wizardPages } from "@src/constants";

const Wrapper = styled.div``;

type State = {
  modalIsOpen: boolean;
  showNewServerModal: boolean;
  selectedServers: number[];
  showConfirmRemove: boolean;
};

type Props = {
  onNavigate: (path: string) => void;
  query: ListQuery;
};

@observer
class MetalHubServersPage extends React.Component<Props, State> {
  state: State = {
    modalIsOpen: false,
    showNewServerModal: false,
    selectedServers: [],
    showConfirmRemove: false,
  };

  pollTimeout = 0;

  stopPolling = false;

  offSwiftRefresh?: () => void;

  componentDidMount() {
    // The top bar's Refresh reloads this page's data.
    this.offSwiftRefresh = onSwiftRefresh(() => {
      this.handleReloadButtonClick();
    });
    document.title = "On-prem servers · SwiftIQ";

    metalHubStore.loadFingerprint();
    projectStore.getProjects();

    this.stopPolling = false;
    this.pollData(true);
  }

  componentWillUnmount() {
    this.offSwiftRefresh?.();
    clearTimeout(this.pollTimeout);
    this.stopPolling = true;
  }

  handleModalOpen() {
    this.setState({ modalIsOpen: true });
  }

  handleModalClose() {
    this.setState({ modalIsOpen: false }, () => {
      this.pollData();
    });
  }

  handleReloadButtonClick() {
    metalHubStore.loadFingerprint();
    metalHubStore.getServers({ showLoading: true });
  }

  handleEmptyListButtonClick() {
    this.setState({ showNewServerModal: true, modalIsOpen: true });
  }

  async handleNewServerDone() {
    this.setState({ showNewServerModal: false, modalIsOpen: false });
  }

  handleRemoveAction() {
    this.setState({ showConfirmRemove: true });
  }

  handleProjectChange() {
    metalHubStore.getServers({ showLoading: true });
  }

  async removeSelectedServers() {
    this.setState({ showConfirmRemove: false });
    await Promise.all(
      this.state.selectedServers.map(async serverId => {
        await metalHubStore.deleteServer(serverId);
      }),
    );
    metalHubStore.getServers({ showLoading: true });
  }

  async refreshServers() {
    await Promise.all(
      this.state.selectedServers.map(async serverId => {
        await metalHubStore.refreshServer(serverId);
      }),
    );
    metalHubStore.getServers({ showLoading: true });
  }

  async create(type: "replica" | "migration") {
    notificationStore.alert(`Preparing the ${type}…`, "info");
    const { selectedServers } = this.state;
    const endpoint = await metalHubStore.getMetalHubEndpoint();

    // Remove the instances from cache so that the wizard has the latest data before it's shown
    instanceSource.removeInstancesFromCache(endpoint.id);
    await instanceStore.loadInstancesInChunks({
      endpoint,
      vmsPerPage: Infinity,
    });
    // find all the selected servers in the background instances
    const instances = instanceStore.backgroundInstances.filter(
      i => selectedServers.map(s => String(s)).indexOf(i.id) > -1,
    );
    if (instances.length !== selectedServers.length) {
      notificationStore.alert(
        `Could not find all instances on endpoint '${endpoint.name}'`,
        "error",
      );
      throw new Error("Instances not found");
    }
    const data: WizardData = {
      source: endpoint,
      selectedInstances: instances,
    };
    this.props.onNavigate(
      `/wizard/${type}/?d=${window.btoa(
        JSON.stringify({
          data,
          currentPage: wizardPages.find(p => p.id === "target"),
        }),
      )}`,
    );
  }

  async pollData(showLoading?: boolean) {
    if (this.state.modalIsOpen || this.stopPolling) {
      return;
    }

    await metalHubStore.getServers({ showLoading, skipLog: true });
    this.pollTimeout = window.setTimeout(() => {
      this.pollData();
    }, configLoader.config.requestPollTimeout);
  }

  get filtersActive() {
    return Boolean(this.props.query.get("q") || this.props.query.get("status"));
  }

  filteredServers(except?: string): MetalHubServer[] {
    const q = this.props.query;
    const text = q.get("q");
    const status = q.get("status");
    return metalHubStore.servers.filter(server => {
      if (except !== "status" && status === "online" && !server.active) return false;
      if (except !== "status" && status === "offline" && server.active) return false;
      if (text) {
        return matchesSearch(text, [
          server.hostname,
          server.api_endpoint,
          serverAddress(server),
          server.os_info?.os_name,
          server.os_info?.os_version,
          ...(server.nics || []).flatMap(n => n.ip_addresses || []),
        ]);
      }
      return true;
    });
  }

  renderToolbar() {
    const q = this.props.query;
    const all = this.filteredServers("status");
    return (
      <>
        <ToolbarSearch value={q.get("q")} placeholder="Search hostname, IP or OS" onChange={v => q.set({ q: v })} />
        <Segmented
          label="Agent status"
          value={q.get("status")}
          onChange={v => q.set({ status: v })}
          options={[
            { value: "", label: "All" },
            { value: "online", label: "Online", count: all.filter(s => s.active).length },
            { value: "offline", label: "Offline", count: all.filter(s => !s.active).length },
          ]}
        />
        {this.filtersActive ? <ClearFiltersButton onClick={() => q.set({ q: null, status: null })} /> : null}
      </>
    );
  }

  rowActions(server: MetalHubServer): DropdownAction[] {
    const withServer = (fn: () => void) => () => this.setState({ selectedServers: [server.id] }, fn);
    return [
      { label: "Open", action: () => this.props.onNavigate(`/bare-metal-servers/${server.id}`) },
      { label: "New replica", disabled: !server.active, action: withServer(() => this.create("replica")) },
      { label: "New migration", disabled: !server.active, action: withServer(() => this.create("migration")) },
      { label: "Refresh", action: withServer(() => this.refreshServers()) },
      { label: "Remove…", color: ThemePalette.alert, action: withServer(() => this.handleRemoveAction()) },
    ];
  }

  renderEmptyListComponent() {
    return metalHubStore.loadingServersError ? (
      <EmptyState
        icon={ServerCrash}
        tone="danger"
        title="The on-prem hub did not answer"
        message={String(metalHubStore.loadingServersError)}
        secondaryLabel="Try again"
        onSecondary={() => this.handleReloadButtonClick()}
      />
    ) : null;
  }

  render() {
    const selectedServers = metalHubStore.servers.filter(s =>
      this.state.selectedServers.includes(s.id),
    );
    const bulkActions: DropdownAction[] = [
      {
        label: "New replica",
        color: ThemePalette.primary,
        disabled: !selectedServers.every(s => s.active),
        action: () => {
          this.create("replica");
        },
      },
      {
        label: "New migration",
        color: ThemePalette.primary,
        disabled: !selectedServers.every(s => s.active),
        action: () => {
          this.create("migration");
        },
      },
      {
        label: "Refresh",
        action: () => {
          this.refreshServers();
        },
      },
      {
        label: "Remove",
        color: ThemePalette.alert,
        action: () => {
          this.handleRemoveAction();
        },
      },
    ];

    return (
      <Wrapper>
        <MainTemplate
          navigationComponent={<Navigation currentPage="bare-metal-servers" />}
          listComponent={
            <FilterList
              toolbar={this.renderToolbar()}
              filtersActive={this.filtersActive}
              onClearFilters={() => this.props.query.set({ q: null, status: null })}
              columns={METAL_HUB_COLUMNS}
              dropdownActions={bulkActions}
              selectionLabel="server"
              countLabel={n => `${n} on-prem server${n === 1 ? "" : "s"}`}
              loading={metalHubStore.loadingServers}
              items={this.filteredServers()}
              onSelectedItemsChange={selectedServers => {
                this.setState({
                  selectedServers: selectedServers.map(s => s.id),
                });
              }}
              listHeaderComponent={
                <MetalHubListHeader
                  visible={this.state.selectedServers.length === 0}
                  fingerprint={metalHubStore.fingerprint}
                  error={metalHubStore.loadingFingerprintError}
                  hideButton={metalHubStore.servers.length === 0}
                  onCreateClick={() => {
                    this.setState({
                      showNewServerModal: true,
                      modalIsOpen: true,
                    });
                  }}
                />
              }
              onItemClick={(server: MetalHubServer) => {
                this.props.onNavigate(`/bare-metal-servers/${server.id}`);
              }}
              onReloadButtonClick={() => {
                this.handleReloadButtonClick();
              }}
              renderItemComponent={props => (
                <MetalHubServerListItem {...props} actions={this.rowActions(props.item)} />
              )}
              emptyListIcon={Server}
              emptyListComponent={this.renderEmptyListComponent()}
              emptyListMessage="No on-prem servers on this hub yet"
              emptyListExtraMessage="Install the SwiftIQ agent on a physical or virtual server in your data centre and register it with the on-prem hub; it then shows here and can be replicated or migrated."
              emptyListButtonLabel="Add on-prem server"
              onEmptyListButtonClick={() => {
                this.handleEmptyListButtonClick();
              }}
            />
          }
          headerComponent={
            <PageHeader
              title="On-prem servers"
              subtitle="Physical servers registered for migration."
              onModalOpen={() => {
                this.handleModalOpen();
              }}
              onModalClose={() => {
                this.handleModalClose();
              }}
              onProjectChange={() => {
                this.handleProjectChange();
              }}
            />
          }
        />
        {this.state.showNewServerModal ? (
          <MetalHubModal
            onUpdateDone={() => {
              this.handleNewServerDone();
            }}
            onRequestClose={() => {
              this.setState({ showNewServerModal: false, modalIsOpen: false });
            }}
          />
        ) : null}
        {this.state.showConfirmRemove ? (
          <AlertModal
            isOpen
            title="Remove the selected on-prem servers?"
            message="Are you sure you want to remove the selected on-prem servers from the hub?"
            extraMessage="&nbsp;"
            onConfirmation={() => {
              this.removeSelectedServers();
            }}
            onRequestClose={() => {
              this.setState({ showConfirmRemove: false });
            }}
          />
        ) : null}
      </Wrapper>
    );
  }
}

function MetalHubServersPageWithNavigate() {
  const navigate = useNavigate();
  const query = useListQuery();

  return <MetalHubServersPage onNavigate={navigate} query={query} />;
}

export default MetalHubServersPageWithNavigate;
